import { useMemo, useState } from 'react'
import { Button, Card, Col, Input, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'

const { Title } = Typography
const { Option } = Select
import productCenter from '../data/productCenter.json'

const methodColors: Record<string, string> = {
  infer: 'blue',
  clothing: 'purple',
  keep: 'green',
  map: 'gold',
  other: 'default',
}

const rootOf = (path: string) => path?.split('/').filter(Boolean)[0] || '(未分类)'

const ProductOverview = () => {
  const [keyword, setKeyword] = useState('')
  const [category, setCategory] = useState<string | undefined>()
  const [method, setMethod] = useState<string | undefined>()

  const products = useMemo(
    () => productCenter.products.map((p: any, index: number) => ({
      ...p,
      key: String(p.id ?? index),
      rootCategory: rootOf(p.newCategory),
    })),
    []
  )

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase()
    return products.filter((p: any) => {
      const hitKeyword = !k || p.name?.toLowerCase().includes(k) || p.sku?.toLowerCase().includes(k)
      const hitCategory = !category || p.rootCategory === category
      const hitMethod = !method || p.method === method
      return hitKeyword && hitCategory && hitMethod
    })
  }, [products, keyword, category, method])

  const exportCsv = () => {
    const headers = ['commodityId', 'sku', 'name', 'oldFullName', 'newFullName', 'method', 'note']
    const rows = filtered.map((p: any) => [
      p.id, p.sku, p.name, p.oldCategory, p.newCategory, p.method, p.note,
    ].map((v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    const blob = new Blob(['\ufeff' + [headers.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `fullhaul-products-${Date.now()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const columns = [
    { title: '商品 ID', dataIndex: 'id', key: 'id', width: 110 },
    { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 200, ellipsis: true },
    { title: '商品名称', dataIndex: 'name', key: 'name', width: 360, ellipsis: true },
    { title: '原分类', dataIndex: 'oldCategory', key: 'oldCategory', width: 200, ellipsis: true },
    {
      title: '新分类',
      dataIndex: 'newCategory',
      key: 'newCategory',
      width: 260,
      ellipsis: true,
      render: (v: string) => <Tag color={v ? 'blue' : 'purple'}>{v || '服装线待指定'}</Tag>,
    },
    {
      title: '分类方法',
      dataIndex: 'method',
      key: 'method',
      width: 110,
      filters: Object.keys(productCenter.stats.methodDistribution).map((v: string) => ({ text: v, value: v })),
      onFilter: (value: any, record: any) => record.method === value,
      render: (v: string) => <Tag color={methodColors[v] || 'default'}>{v}</Tag>,
    },
  ]

  return (
    <div>
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={6}><Card><Statistic title="商品总数" value={productCenter.stats.total} /></Card></Col>
        <Col xs={24} sm={6}><Card><Statistic title="分类根目录" value={productCenter.stats.categoryCount} /></Card></Col>
        <Col xs={24} sm={6}><Card><Statistic title="已指定新分类" value={productCenter.products.filter((p: any) => p.newCategory).length} /></Card></Col>
        <Col xs={24} sm={6}><Card><Statistic title="服装线待指定" value={productCenter.stats.methodDistribution.clothing || 0} /></Card></Col>
      </Row>

      <Card title="商品中心 · 商品总览" extra={<Space><Button icon={<DownloadOutlined />} onClick={exportCsv}>导出筛选结果</Button></Space>}>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Space wrap>
            <Input allowClear prefix={<SearchOutlined />} placeholder="搜索 SKU / 商品名称" value={keyword} onChange={e => setKeyword(e.target.value)} style={{ width: 280 }} />
            <Select allowClear placeholder="按根分类" style={{ width: 180 }} value={category} onChange={setCategory}>
              {productCenter.categories.map((c: any) => <Option key={c.name} value={c.name}>{`${c.name}（${c.count}）`}</Option>)}
            </Select>
            <Select allowClear placeholder="按分类方法" style={{ width: 160 }} value={method} onChange={setMethod}>
              {Object.entries(productCenter.stats.methodDistribution).map(([v, n]: any) => <Option key={v} value={v}>{`${v}（${n}）`}</Option>)}
            </Select>
            <span>共 {filtered.length} 条</span>
          </Space>
          <Table columns={columns} dataSource={filtered} scroll={{ x: 1240 }} size="middle" pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }} />
        </Space>
      </Card>
    </div>
  )
}

export default ProductOverview

